/**
 * Client Providers — App Router
 *
 * Migrated from _app.tsx. All client-side providers are consolidated here.
 * This is a Client Component because providers use React context, state, etc.
 *
 * Performance: Heavy third-party hooks (OneSignal, ServiceWorker, IndexedDB)
 * are deferred into a DeferredInitialization component loaded via dynamic(ssr:false)
 * to reduce Total Blocking Time (TBT).
 */
'use client';

// Initialize i18n before any component renders (replaces appWithTranslation)
import '@/lib/i18n';

import React from 'react';
import dynamic from 'next/dynamic';
import {StoreViewProvider} from '@/_application/modules/stores/store-view.context';
import QueryProvider from '@/framework/client/query-provider';
import {SessionAuthProvider} from '@/_application/core/components/ui/session-auth/session-auth.context';
import {SearchProvider} from '@/_application/core/components/ui/search/search.context';
import {ModalProvider} from '@/_application/core/components/ui/modal/modal.context';
import {CartProvider} from '@kidslabs/module-quote';
import {GoogleTagManager} from '@next/third-parties/google';
import {StoreCodeChangeRegistrations} from './store-code-change-registrations';

const ManagedModal = dynamic(() => import('@/_application/core/components/ui/modal/managed-modal'), {ssr: false});
const ToastContainer = dynamic(() => import('react-toastify').then(m => ({default: m.ToastContainer})), {ssr: false});

/**
 * DeferredInitialization — loaded after hydration to reduce TBT.
 * Contains: OneSignal, ServiceWorker, IndexedDB persistence,
 * NofollowObserver, ViewPageTracking.
 */
const DeferredInitialization = dynamic(
    () => import('./deferred-initialization').then(m => ({default: m.DeferredInitialization})),
    {ssr: false},
);

interface ProvidersProps {
    children: React.ReactNode;
    isMobile: boolean;
    layoutData: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        categories: any;
        storesNumber: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        brands: any;
    };
}

export function Providers({children}: Readonly<ProvidersProps>) {
    return (
        <>
            <GoogleTagManager gtmId="GTM-5XLWZ4S7" />
            <ModalProvider>
                <StoreViewProvider>
                    <QueryProvider pageProps={{}}>
                        <SessionAuthProvider>
                            <SearchProvider>
                                <CartProvider>
                                    <StoreCodeChangeRegistrations />
                                    <DeferredInitialization />
                                    {children}
                                    <ManagedModal />
                                    <ToastContainer autoClose={3000} theme="colored" />
                                </CartProvider>
                            </SearchProvider>
                        </SessionAuthProvider>
                    </QueryProvider>
                </StoreViewProvider>
            </ModalProvider>
        </>
    );
}
